import { Box, Tab, Tabs } from '@mui/material';
import { connect } from 'react-redux';
import { get, groupBy, isEmpty, keyBy, omit, orderBy, unionBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

// Commons
import { Button, Dropdown, INPUT_WIDTH } from '@commons/utils/styledLibraryComponents';
import { CATEGORY_TYPES_OBJECT } from '@commons/constants/categoryTypes';
import {
  convertEntityQuantityToInpulseUnit,
  getFactorToConvertMetricToEntityUnit,
} from '@commons/utils/conversion';
import { DATE_DISPLAY_FORMATS, PRODUCTS_CALENDAR_INFO } from '@commons/DatePickers/constants';
import {
  DropdownItem,
  DropdownItemImage,
  DropdownItemMainValue,
  DropdownItemSecondaryValue,
  DropdownItemValuesContainer,
} from '@commons/TableForm/TableFormInModal/styledComponents.js';
import {
  GenericContentContainer,
  GenericGapContainer,
  GenericHeaderContainer,
} from '@commons/Layout/styledComponents';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getFilteredStoresWithSelectedGroups } from '@commons/ExportModalWithStoresAndPeriod/utils';
import { getIsCentralMode } from '@commons/utils/localStorage';
import { getTheme } from '@commons/utils/theme';
import { getUniqueGroupsOfStores } from '@commons/utils/groups';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { LOSS_TYPE } from '@commons/utils/losses';
import { PastDayDatePicker } from '@commons/DatePickers/PastDayDatePicker';
import { TabPanel } from '@commons/TabPanel';
import KpiDisplay from '@commons/KpiDisplay';

// Services
import { lossService } from '@services/loss';
import { storeSupplierProductMapping as storeSupplierProductMappingService } from '@services/storeSupplierProductMapping';
import { supplier as supplierService } from '@services/supplier';
import { upload as uploadService } from '@services/upload';
import centralService from '@services/central';
import recipeService from '@services/recipe';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

// Selectors
import {
  canCorrectCentralKitchenLoss,
  canCorrectSalesPointLoss,
  canCreateCentralKitchenLoss,
  canCreateOrUpdateSalesPointLoss,
} from '@selectors/actions/lossActions';
import { getAuthorizedActions } from '@selectors/featureProps';
import { getCentralKitchenStores, getSalesPointStores } from '@selectors/stores';
import { getClientInfo } from '@selectors/client';

// Miscellaneous
import { convertSPPQuantityInMasterUnit } from '@orders/utils/computePackagingToSupplierProduct';
import { getActions } from './utils/actions';
import { getDatesWithLossesForMonth } from './utils/commons';
import { getExportModalConfig } from './utils/export/lossesExportModal';
import { getLossesExportXLS } from './utils/export/getExportxlsx';
import { useCategoriesBatchUpdateModal } from '@hooks/useCategoriesBatchUpdateModal';

// Components and styles
import { Container, getTabStyles } from './styledComponents';
import DisplayImgInFullScreen from '@commons/DisplayImgInFullScreen';
import RecipesGrid from './recipesGrid';
import SupplierProductsGrid from './supplierProductsGrid';

const LOSSES_TYPE_BY_SELECTED_TAB = {
  0: LOSS_TYPE.SUPPLIER_PRODUCT,
  1: LOSS_TYPE.RECIPE,
};

const LossDataGrid = (props) => {
  const {
    currency,
    stores,
    showErrorMessage,
    showSuccessMessage,
    client: {
      defaultChannelId,
      hasMultipleChannels,
      clientId,
      storeName,
      defaultTimezone,
      clientChannels,
    },
    pageLoading,
    pageLoaded,
    authorizedSalesPointActions,
    authorizedCentralKitchenActions,
    openGenericModal,
    refreshGenericModal,
    modal,
  } = props;

  const clientStoreNamePlural = getClientStoreNameTranslation(storeName, true);

  const isCentralMode = getIsCentralMode();
  const theme = getTheme();
  const canCreateOrUpdateLosses = isCentralMode
    ? canCreateCentralKitchenLoss(authorizedCentralKitchenActions)
    : canCreateOrUpdateSalesPointLoss(authorizedSalesPointActions);

  const [actions, setActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedStore, setSelectedStore] = useState(stores[0] || {});
  const [selectedDate, setSelectedDate] = useState(moment.tz(selectedStore.timezone));
  const [focusedMonth, setFocusedMonth] = useState(moment());
  const [isDirty, setIsDirty] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [lossCategories, setLossCategories] = useState([]);
  // The list of dates on which losses have been reported in the last 48h
  const [recentDatesWithLosses, setRecentDatesWithLosses] = useState([]);
  // The list of dates on which losses have been reported more than 48h ago
  const [oldDatesWithLosses, setOldDatesWithLosses] = useState([]);

  // Export modal states
  const [exportStores, setExportStores] = useState(stores);
  const [exportSelectedStores, setExportSelectedStores] = useState(null);
  const [exportGroups, setExportGroups] = useState([]);
  const [exportSelectedGroups, setExportSelectedGroups] = useState([]);
  const [exportPeriod, setExportPeriod] = useState({
    startDate: null,
    endDate: null,
  });

  // Supplier Products
  const [supplierProductRows, setSupplierProductRows] = useState([]);
  const [supplierProductsTotal, setSupplierProductsTotal] = useState(0);
  const [supplierProductsDropdownItems, setSupplierProductsDropdownItems] = useState([]);

  // Recipes
  const [recipeRows, setRecipeRows] = useState([]);
  const [recipesTotal, setRecipesTotal] = useState(0);
  const [recipesDropdownItems, setRecipesDropdownItems] = useState([]);
  const [channels, setChannels] = useState([]);

  const [selectedTab, setSelectedTab] = useState(0);
  const [shouldPictureFullScreen, setShouldPictureFullScreen] = useState({ state: false });

  const [canCorrectLosses, setCanCorrectLosses] = useState(false);

  useEffect(() => {
    if (stores.length) {
      const uniqGroups = getUniqueGroupsOfStores(stores);

      setExportGroups(uniqGroups);
      setExportSelectedGroups(uniqGroups);
    }
  }, []);

  useEffect(() => {
    if (!exportSelectedGroups.length) {
      return;
    }

    const filteredStores = getFilteredStoresWithSelectedGroups(exportSelectedGroups, stores);

    // Initial list of stores to export
    setExportStores(filteredStores);

    // Selected stores to export
    setExportSelectedStores(filteredStores);
  }, [exportSelectedGroups]);

  useEffect(() => {
    const disableCategoriesUpdate = isDirty || hasErrors;

    const actions = getActions({
      openLossCategoriesBatchUpdateModal: lossCategoriesBatchUpdateModal.open,
      authorizedSalesPointActions,
      authorizedCentralKitchenActions,
      isCentralMode,
      openLossesExportModal,
      disableCategoriesUpdate,
    });

    setActions(actions);

    const isModalOpened = get(modal, 'GenericModalBool', false);

    if (!isModalOpened) {
      return;
    }

    const config = getExportModalConfig({
      clientStoreName: clientStoreNamePlural,
      defaultTimezone,
      stores: exportStores,
      groups: exportGroups,
      selectedGroups: exportSelectedGroups,
      selectedStoresForExport: exportSelectedStores,
      setSelectedGroups: setExportSelectedGroups,
      setSelectedStoresForExport: setExportSelectedStores,
      handleCloseCleanUp,
      setExportPeriod,
      exportPeriod,
      handleExport,
    });

    refreshGenericModal(config);
  }, [
    exportStores,
    exportSelectedStores,
    exportPeriod,
    isDirty,
    hasErrors,
    // For the reload after categories update
    selectedDate,
    selectedStore,
  ]);

  useEffect(() => {
    setIsDirty(false);
    loadData();
  }, [selectedStore, selectedDate]);

  useEffect(() => {
    getPastDatesWithLossesForMonth(focusedMonth, selectedStore, selectedTab);
  }, [focusedMonth, selectedStore, selectedTab]);

  useEffect(() => {
    setTotal(supplierProductsTotal + recipesTotal);
  }, [supplierProductsTotal, recipesTotal]);

  useEffect(() => {
    setHasErrors(
      supplierProductRows.some(({ quantity }) => quantity <= 0) ||
        recipeRows.some(({ quantity }) => quantity <= 0),
    );
  }, [supplierProductRows, recipeRows]);

  useEffect(() => {
    const isCurrentDateForbidden = oldDatesWithLosses.some((date) =>
      moment(date).isSame(selectedDate, 'day'),
    );

    setCanCorrectLosses(
      !isCurrentDateForbidden ||
        (isCurrentDateForbidden && isCentralMode
          ? canCorrectCentralKitchenLoss(authorizedCentralKitchenActions)
          : canCorrectSalesPointLoss(authorizedSalesPointActions)),
    );
  }, [oldDatesWithLosses, selectedDate]);

  const handleCloseCleanUp = () => {
    if (stores.length) {
      const uniqGroups = getUniqueGroupsOfStores(stores);

      setExportGroups(uniqGroups);
      setExportSelectedGroups(uniqGroups);
    }
  };

  /**
   * Computes the price per packaging unit based on the SP price and its packagings.
   * To ensure backward-compatibility (if packagings change in the meantime), if the `lossPackagingId` is provided,
   * it is used as the reference for unit price computation. Otherwise, the current invoice packaging is used.
   *
   * @param {Array<Object>} packagings
   * @param {number} price - Either the price of the loss or the current supplier product price.
   * @param {string | null} [lossPackagingId] - (Optional) The id of the packaging used when the loss was created.
   *
   * @returns {Object} An object mapping packaging IDs to their computed unit price.
   */
  const getPriceByPackagingId = (packagings, price, lossPackagingId = null) => {
    const invoicePackaging = packagings.find(({ isUsedInInvoice }) => !!isUsedInInvoice);

    const invoicePackagingId = lossPackagingId ? lossPackagingId : invoicePackaging.id;

    return packagings.reduce((result, packaging) => {
      if (result[packaging.id] === undefined) {
        const computedPrice =
          price / convertSPPQuantityInMasterUnit(invoicePackagingId, packagings);

        const convertedQuantity = convertSPPQuantityInMasterUnit(packaging.id, packagings);

        result[packaging.id] = computedPrice * convertedQuantity;
      }

      return result;
    }, {});
  };

  const loadData = async () => {
    setIsLoading(true);
    pageLoading();

    const channels = await fetchChannels();

    const [{ supplierProductLosses, recipeLosses }, lossCategories] = await Promise.all([
      fetchLosses(channels),
      getLossCategories(),
    ]);

    await Promise.all([
      fetchSupplierProducts(supplierProductLosses, lossCategories),
      fetchRecipes(recipeLosses, channels, lossCategories),
    ]);
    setIsLoading(false);
    pageLoaded();
  };

  const fetchChannels = async () => {
    try {
      const allTranslatedChannels = clientChannels.map((channel) => ({
        ...channel,
        name: i18next.t(`${channel.translationKey}.FULL`),
      }));

      if (!hasMultipleChannels) {
        const defaultChannel = allTranslatedChannels.find(({ id }) => id === defaultChannelId);

        setChannels([defaultChannel]);

        return [defaultChannel];
      }

      setChannels(allTranslatedChannels);

      return allTranslatedChannels;
    } catch {
      showErrorMessage(i18next.t('GENERAL.FETCH_CHANNELS_FAILURE'));
      return [];
    }
  };

  const fetchLosses = async (channels, isExport = false) => {
    try {
      const storeIds = isExport ? exportSelectedStores.map(({ id }) => id) : [selectedStore.id];
      const startDate = moment(isExport ? exportPeriod.startDate : selectedDate).format(
        DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
      );
      const endDate = moment(isExport ? exportPeriod.endDate : selectedDate).format(
        DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
      );

      const { supplierProductLosses, entityLosses } = await lossService.getLossesByStoreIdAndDates(
        storeIds,
        startDate,
        endDate,
        // If is an export, ignore pictures
        { withPictures: !isExport },
      );

      const formattedSupplierProductLosses = supplierProductLosses.map((loss) => {
        const priceByPackagingId = getPriceByPackagingId(
          loss.packagings,
          loss.price,
          loss.supplierProductPackagingId,
        );

        const cost = priceByPackagingId[loss.supplierProductPackagingId];

        const total = cost * loss.quantity;

        const storeName = exportSelectedStores?.find(({ id }) => id === loss.storeId)?.name;

        return {
          ...loss,
          isRowSelected: false,
          priceByPackagingId,
          name: loss.supplierProductName,
          unit: loss.supplierProductUnit,
          img: loss.supplierProductImg,
          packagings: loss.packagings.filter(({ isUsedInStock }) => isUsedInStock),
          key: `${loss.supplierProductPackagingId}-${loss.lossCategoryId}`,
          cost,
          total,
          storeName,
        };
      });

      const channelKeyByIds = keyBy(channels, 'id');

      const formattedRecipeLosses = entityLosses.map((loss) => {
        const recipeChannelIds = Object.keys(loss.costByChannelId);

        const recipeChannels = recipeChannelIds
          .map((channelId) => channelKeyByIds[channelId])
          // Remove falsy values
          .filter(Boolean);

        const costByChannelId = loss.costByChannelId[loss.channelId];

        const total =
          costByChannelId && hasMultipleChannels
            ? costByChannelId * loss.quantity
            : loss.cost * loss.quantity;

        const storeName = exportSelectedStores?.find(({ id }) => id === loss.storeId)?.name;

        return {
          ...loss,
          channels: recipeChannels,
          isRowSelected: false,
          recipeId: loss.entityId,
          img: loss.recipeImg,
          name: loss.recipeName,
          unit: loss.recipeUnit,
          key: `${loss.channelId}-${loss.lossCategoryId}`,
          total,
          storeName,
        };
      });

      /*
        We only set the supplierProductRows and not the recipeRows here because we need data from
        the client recipes to format the recipeRows by merging the channels (see formatRecipes fnc)
      */
      if (!isExport) {
        setSupplierProductRows(orderBy(formattedSupplierProductLosses, 'total', 'desc'));
      }

      return {
        supplierProductLosses: formattedSupplierProductLosses,
        recipeLosses: formattedRecipeLosses,
      };
    } catch {
      showErrorMessage(i18next.t('LOSSES.LIST.FETCH_FAILURE'));

      return {
        supplierProductLosses: [],
        recipeLosses: [],
      };
    }
  };

  const renderDropdownItem = (img, name, supplierName) => (
    <DropdownItem>
      <DropdownItemImage src={img}></DropdownItemImage>
      <DropdownItemValuesContainer>
        <DropdownItemMainValue>{name}</DropdownItemMainValue>
        <DropdownItemSecondaryValue>{supplierName}</DropdownItemSecondaryValue>
      </DropdownItemValuesContainer>
    </DropdownItem>
  );

  const formatMappingsIntoSupplierProducts = (
    storeSupplierProductMappings,
    suppliersOfStore,
    existingSupplierProductLosses,
    lossCategories,
  ) => {
    const supplierProductLossesGroupedBySPId = groupBy(
      existingSupplierProductLosses,
      'supplierProductId',
    );

    const formattedSupplierProducts = storeSupplierProductMappings.map((mapping) => {
      const supplierProductRel = mapping.lnkSupplierproductStoresupplierproductmappingrel;

      const existingLossesForSameSP = supplierProductLossesGroupedBySPId[supplierProductRel.id];

      if (existingLossesForSameSP) {
        // We take the first loss because they all have the same sp infos and this is what we're looking for
        const firstExistingLoss = existingLossesForSameSP[0];

        /*
          We check if the number of existing losses is equal to the number of loss categories * the number of packagings to
          see if all possibilities have already been created, if so we disable the row in the dropdown
        */
        const isDisabled =
          existingLossesForSameSP.length ===
          lossCategories.length * firstExistingLoss.packagings.length;

        return {
          ...firstExistingLoss,
          quantity: 1,
          supplierProductPackagingId: null,
          lossCategoryId: null,
          isDisabled,
          /**
           * The key value will be later used as a composite key with the format: "{packagingId}-{lossCategoryId}"
           * its purpose is to check if a packaging has been used with all possible loss categories
           * A packaging is considered "full" when it has been used with all possible loss categories
           */
          key: null,
          value: `${supplierProductRel.name} - ${supplierProductRel.sku} - ${supplierProductRel.ean}`,
          renderValue: () =>
            renderDropdownItem(
              firstExistingLoss.img || '/images/inpulse/supplier-product-photo-placeholder.svg',
              firstExistingLoss.supplierProductName,
              firstExistingLoss.supplierName,
            ),
        };
      }

      const supplierKeyById = keyBy(suppliersOfStore, 'id');
      const supplierName = get(supplierKeyById, `[${supplierProductRel.supplierId}].name`, '');

      const priceByPackagingId = getPriceByPackagingId(
        supplierProductRel.packagings,
        supplierProductRel.price,
      );

      return {
        quantity: 1,
        isRowSelected: false,
        supplierProductId: supplierProductRel.id,
        name: supplierProductRel.name,
        img: supplierProductRel.img,
        supplierProductPackagingId: null,
        lossCategoryId: null,
        packagings: supplierProductRel.packagings.filter(({ isUsedInStock }) => isUsedInStock),
        price: supplierProductRel.price,
        sku: supplierProductRel.sku,
        supplierName,
        /**
         * The key value will be later used as a composite key with the format: "{packagingId}-{lossCategoryId}"
         * its purpose is to check if a packaging has been used with all possible loss categories
         * A packaging is considered "full" when it has been used with all possible loss categories
         */
        key: null,
        priceByPackagingId,
        value: `${supplierProductRel.name} - ${supplierProductRel.sku} - ${supplierProductRel.ean}`,
        renderValue: () =>
          renderDropdownItem(
            supplierProductRel.img || '/images/inpulse/supplier-product-photo-placeholder.svg',
            supplierProductRel.name,
            supplierName,
          ),
      };
    });

    return formattedSupplierProducts;
  };

  const fetchSupplierProducts = async (existingSupplierProductLosses, lossCategories) => {
    try {
      const [storeSupplierProductMappings, suppliersOfStore] = await Promise.all([
        storeSupplierProductMappingService.getStoreSupplierProductMappingsOfStore(
          selectedStore.id,
          moment().startOf('day').format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          false, // filterByUserCatalog
          true, // withTranslations
        ),
        supplierService.getSuppliersOfStore(selectedStore.id),
      ]);

      if (isEmpty(storeSupplierProductMappings)) {
        return;
      }

      const filteredStoreSupplierProductMappings = storeSupplierProductMappings.filter(
        (storeSupplierProductMapping) =>
          storeSupplierProductMapping.hasDlc &&
          get(
            storeSupplierProductMapping,
            'lnkSupplierproductStoresupplierproductmappingrel.active',
            false,
          ),
      );

      const formattedSPs = formatMappingsIntoSupplierProducts(
        filteredStoreSupplierProductMappings,
        suppliersOfStore,
        existingSupplierProductLosses,
        lossCategories,
      );

      setSupplierProductsDropdownItems(formattedSPs);
    } catch {
      showErrorMessage(i18next.t('GENERAL.FETCH_SUPPLIER_PRODUCTS_FAILURE'));
    }
  };

  const formatRecipeLossesAndDropdown = (recipes, recipeLosses, channels, lossCategories) => {
    const channelKeyByIds = keyBy(channels, 'id');

    const recipeLossesGroupedByEntityId = groupBy(recipeLosses, 'entityId');

    // Intentionally display cashier loss related to ingredient in recipe losses
    const existingCashierLossForIngredients = recipeLosses.reduce((acc, entityLoss) => {
      if (!acc[entityLoss.id] && entityLoss.isIngredient && entityLoss.isCashierLoss) {
        acc[entityLoss.id] = entityLoss;
      }

      return acc;
    }, {});

    const formattedExistingRecipeLosses = Object.values(existingCashierLossForIngredients);

    const dropdownRecipes = recipes.reduce((acc, recipe) => {
      const existingLossesForSameRecipe = recipeLossesGroupedByEntityId[recipe.id];

      const currentRecipeChannelIds = Object.keys(recipe.costByChannelId);

      const currentRecipeChannels = currentRecipeChannelIds
        .map((channelId) => channelKeyByIds[channelId])
        // Remove falsy values
        .filter(Boolean);

      if (existingLossesForSameRecipe) {
        /*
          When a recipe has multiple channels but losses are only created for some of them,
          the other channels should still be selectable when editing or adding more losses.

          This is why we merge the current recipe channels (currentRecipeChannels) with the
          channels from the first existing loss (firstExistingLoss.channels).

          This ensures that all possible channels remain available, even if some haven’t been used yet.
        */
        const firstExistingLoss = existingLossesForSameRecipe[0];
        const mergedChannels = unionBy(firstExistingLoss.channels, currentRecipeChannels, 'id');

        const mergedCostByChannelId = {
          ...recipe.costByChannelId,
          ...firstExistingLoss.costByChannelId,
        };

        // Formatting existing losses to have the merged channels and costByChannelId
        existingLossesForSameRecipe.forEach((loss) => {
          formattedExistingRecipeLosses.push({
            ...loss,
            quantity: convertEntityQuantityToInpulseUnit(loss.quantity, loss.unit),
            channels: mergedChannels,
            costByChannelId: mergedCostByChannelId,
          });
        });

        /*
          If the total number of existing losses for a recipe matches the total number of possible
          combinations (number of channels * number of loss categories), disables the row to prevent
          the user from creating duplicate losses
        */
        const isDisabled =
          existingLossesForSameRecipe.length === mergedChannels.length * lossCategories.length;

        if (!recipe.hasLoss || !recipe.active) {
          return acc;
        }

        acc.push({
          ...firstExistingLoss,
          channels: mergedChannels,
          costByChannelId: mergedCostByChannelId,
          id: null,
          isRowSelected: false,
          quantity: 1,
          lossCategoryId: null,
          /*
            If client hasMultipleChannels is true we set the channelId to null because the channel will be selected later
            in the channels dropdown but if client hasMultipleChannels is false we set the channelId to the default channelId
            because the channels dropdown will be disabled meaning it's the only available channel
          */
          channelId: hasMultipleChannels ? null : defaultChannelId,
          key: null,
          isDisabled,
          renderValue: () =>
            renderDropdownItem(
              firstExistingLoss.img || '/images/inpulse/recipe-photo-placeholder.svg',
              firstExistingLoss.name,
              firstExistingLoss.category,
            ),
        });

        return acc;
      }

      if (!recipe.hasLoss || !recipe.active) {
        return acc;
      }

      acc.push({
        recipeId: recipe.id,
        name: recipe.name,
        img: recipe.img,
        unit: recipe.unit,
        /*
          If client hasMultipleChannels is true we set the channelId to null because the channel will be selected later
          in the channels dropdown but if client hasMultipleChannels is false we set the channelId to the default channelId
          because the channels dropdown will be disabled meaning it's the only available channel
        */
        channelId: hasMultipleChannels ? null : defaultChannelId,
        channels: currentRecipeChannels,
        quantity: 1,
        cost: recipe.cost,
        costByChannelId: recipe.costByChannelId || {},
        isRowSelected: false,
        key: null,
        renderValue: () =>
          renderDropdownItem(
            recipe.img || '/images/inpulse/recipe-photo-placeholder.svg',
            recipe.name,
            recipe.category,
          ),
      });

      return acc;
    }, []);

    return {
      dropdownRecipes,
      formattedRecipeLosses: orderBy(formattedExistingRecipeLosses, 'total', 'desc'),
    };
  };

  const fetchRecipes = async (recipeLosses, channels, lossCategories) => {
    try {
      const recipes = isCentralMode
        ? await centralService.getKitchenRecipesOfClient(
            clientId,
            true, // computeRecipesCost
            false, // onlyAvailableInStocks
            true, // withTranslations
          )
        : await recipeService.getRecipesOfClient({
            computeRecipesCost: true,
            onlyAvailableInStocks: false,
            withTranslations: true,
            storeId: get(selectedStore, 'id', null),
          });

      const { dropdownRecipes, formattedRecipeLosses } = formatRecipeLossesAndDropdown(
        recipes,
        recipeLosses,
        channels,
        lossCategories,
      );

      setRecipeRows(formattedRecipeLosses);
      setRecipesDropdownItems(dropdownRecipes);
    } catch {
      showErrorMessage(i18next.t('LOSSES.RECIPES.SALES_POINT_FETCH_FAILURE'));
      setRecipesDropdownItems([]);
    }
  };

  const getLossCategories = async () => {
    try {
      const allLossCategories = await lossService.getLossCategoriesByClientId(clientId);

      const formattedLossCategories = [
        { id: null, value: i18next.t('GENERAL.NONE_VALUE') },
        ...allLossCategories.map((category) => ({
          ...category,
          value: category.name,
        })),
      ];

      setLossCategories(orderBy(formattedLossCategories, 'value'));

      return formattedLossCategories;
    } catch {
      showErrorMessage(i18next.t('GENERAL.FETCH_LOSS_CATEGORIES_FAILURE'));
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    pageLoading();
    try {
      const formattedSupplierProductRows = supplierProductRows.map((row) => ({
        ...omit(row, 'id'),
        price: get(row, `priceByPackagingId[${row.supplierProductPackagingId}]`, row.price),
        lossType: LOSS_TYPE.SUPPLIER_PRODUCT,
      }));

      const formattedRecipeRows = recipeRows.map((row) => {
        const price = row.costByChannelId[row.channelId];

        return {
          ...omit(row, 'id'),
          entityId: row.recipeId,
          lossType: LOSS_TYPE.RECIPE,
          price,
          quantity: row.quantity * getFactorToConvertMetricToEntityUnit(row.unit),
        };
      });

      const losses = [...formattedSupplierProductRows, ...formattedRecipeRows];

      const uploadImagesPromises = losses.map(async (loss) => {
        // If there is an image, it means the user either updated or added a new image to the loss row
        if (loss.lossPictureImage) {
          try {
            const { data } = await uploadService.uploadFile(loss.lossPictureImage.file, {
              clientId,
              keepOriginalFileName: true,
              type: 'loss',
              storeId: selectedStore.id,
            });

            const lossPictureUrl = get(data, 'fileUri', null);

            return { ...loss, lossPictureUrl };
          } catch {
            // Fallback in case of error
            return { ...loss, lossPictureUrl: null };
          }
        }

        return loss;
      });

      const lossesWithImages = await Promise.all(uploadImagesPromises);

      await lossService.upsertByBatch(
        lossesWithImages,
        selectedStore.id,
        moment(selectedDate).format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
      );

      showSuccessMessage(i18next.t('LOSSES.LIST.SAVE_SUCCESS'));
      getPastDatesWithLossesForMonth(focusedMonth, selectedStore, selectedTab);
      setIsDirty(false);
    } catch {
      showErrorMessage(i18next.t('LOSSES.LIST.SAVE_FAILURE'));
    } finally {
      pageLoaded();
      setIsLoading(false);
    }
  };

  const getPastDatesWithLossesForMonth = async (
    currentFocusedMonth,
    currentSelectedStore,
    selectedTab,
  ) => {
    pageLoading();
    setIsLoading(true);
    try {
      const datesWithLosses = await getDatesWithLossesForMonth(
        currentFocusedMonth,
        currentSelectedStore,
        LOSSES_TYPE_BY_SELECTED_TAB[selectedTab],
      );

      setOldDatesWithLosses(get(datesWithLosses, 'oldDatesWithLosses', []));
      setRecentDatesWithLosses(get(datesWithLosses, 'recentDatesWithLosses', []));
    } catch {
      showErrorMessage(i18next.t('LOSSES.LIST.RETRIEVE_DATES_FAILURE'));
    } finally {
      setIsLoading(false);
      pageLoaded();
    }
  };

  const handleTabChange = (event, tabValue) => {
    setSelectedTab(tabValue);
  };

  const renderPictureFullscreen = () => {
    if (!shouldPictureFullScreen.state) {
      return <></>;
    }

    return (
      <DisplayImgInFullScreen
        image={shouldPictureFullScreen.img}
        setDisplayProductPicture={setShouldPictureFullScreen}
      />
    );
  };

  const openLossesExportModal = () => {
    const config = getExportModalConfig({
      clientStoreName: clientStoreNamePlural,
      defaultTimezone,
      stores: exportStores,
      groups: exportGroups,
      selectedGroups: exportSelectedGroups,
      selectedStoresForExport: exportSelectedStores,
      setSelectedGroups: setExportSelectedGroups,
      setSelectedStoresForExport: setExportSelectedStores,
      handleCloseCleanUp,
      setExportPeriod,
      exportPeriod,
      handleExport,
    });

    openGenericModal(config);
  };

  const handleExport = async () => {
    pageLoading();
    setIsLoading(true);
    try {
      const { supplierProductLosses, recipeLosses } = await fetchLosses(channels, true);

      const orderedSupplierProductLosses = orderBy(
        supplierProductLosses,
        ['timestamp', 'total'],
        'desc',
      );

      const orderedRecipeLosses = orderBy(recipeLosses, ['timestamp', 'total'], 'desc');

      getLossesExportXLS({
        supplierProductsLosses: orderedSupplierProductLosses,
        currency,
        lossCategories,
        isCentralMode,
        hasMultipleChannels,
        recipesLosses: orderedRecipeLosses,
        channels,
        clientStoreName: clientStoreNamePlural,
        selectedStores: exportSelectedStores,
        startDate: exportPeriod.startDate,
        endDate: exportPeriod.endDate,
        defaultChannelId,
      });
    } catch {
      showErrorMessage(i18next.t('LOSSES.LIST.EXPORT_FAILURE'));
    } finally {
      setIsLoading(false);
      pageLoaded();
    }
  };

  const lossCategoriesBatchUpdateModal = useCategoriesBatchUpdateModal({
    onRefresh: loadData,
    loadProps: {
      clientId,
      type: CATEGORY_TYPES_OBJECT.LOSS,
      propertyType: 'categories',
    },
    itemListSorterParams: {
      disableMergeItems: true,
      disableCreateItem: false,
      entityName: i18next.t('GENERAL.LOSSES'),
      createItemLabel: i18next.t('ADMIN.GENERAL.MANAGE_CATEGORIES_CREATE_LABEL'),
      checkForPropertyNone: false,
      hideAssociatedItems: true,
    },
  });

  return (
    <Container>
      <GenericHeaderContainer>
        <LeftRightSplitter
          left={
            <GenericGapContainer>
              <Dropdown
                iconSrc={'/images/inpulse/central-black-small.svg'}
                isDisabled={isLoading}
                items={stores}
                searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                selectedItem={selectedStore}
                width={INPUT_WIDTH.LARGE}
                isRequired
                onSelectionChange={setSelectedStore}
              />

              <PastDayDatePicker
                authorizedDates={recentDatesWithLosses}
                calendarInfo={PRODUCTS_CALENDAR_INFO()}
                date={selectedDate}
                disabled={isLoading}
                forbiddenDates={oldDatesWithLosses}
                maxFutureDate={moment.tz(selectedStore.timezone)}
                timezone={selectedStore.timezone}
                clickableForbiddenDates
                onDateChange={(value) => {
                  setSelectedDate(value);
                }}
                onNextMonthClick={(nextMonth) => setFocusedMonth(nextMonth)}
                onPrevMonthClick={(prevMonth) => setFocusedMonth(prevMonth)}
              />

              <KpiDisplay
                currency={currency}
                displayCurrencyCode={false}
                isLoading={isLoading}
                label={i18next.t('GENERAL.TOTAL_EXCL_TAXES_WITH_CURRENCY', {
                  currencyCode: currency.alphabeticCode,
                })}
                value={total}
                withoutDecimals={false}
              />
            </GenericGapContainer>
          }
          right={
            <GenericGapContainer>
              <Button
                actions={actions}
                color={'inpulse-outline'}
                iconDropdown={'/images/inpulse/carret-black.svg'}
                iconOnLeft={false}
                label={i18next.t('GENERAL.ACTIONS')}
              />

              <Button
                color={'inpulse-default'}
                handleClick={handleSave}
                icon={'/images/inpulse/save-white-small.svg'}
                isDisabled={isLoading || !isDirty || hasErrors}
                label={i18next.t('GENERAL.SAVE')}
              />
            </GenericGapContainer>
          }
        />
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs aria-label="losses tabs" value={selectedTab} onChange={handleTabChange}>
            <Tab label={i18next.t('GENERAL.SUPPLIER_PRODUCT_PLURAL')} sx={getTabStyles(theme)} />
            <Tab label={i18next.t('GENERAL.RECIPE_PLURAL')} sx={getTabStyles(theme)} />
          </Tabs>
        </Box>
      </GenericHeaderContainer>
      <GenericContentContainer padding={'0px 24px 16px 24px'}>
        <TabPanel index={0} value={selectedTab}>
          <SupplierProductsGrid
            canCorrectLosses={canCorrectLosses}
            canCreateOrUpdateLosses={canCreateOrUpdateLosses}
            currency={currency}
            forbiddenDates={oldDatesWithLosses}
            isLoading={isLoading}
            lossCategories={lossCategories}
            selectedDate={selectedDate}
            selectedStore={selectedStore}
            setIsDirty={setIsDirty}
            setShouldPictureFullScreen={setShouldPictureFullScreen}
            setSupplierProductRows={setSupplierProductRows}
            setSupplierProductsDropdownItems={setSupplierProductsDropdownItems}
            setSupplierProductsTotal={setSupplierProductsTotal}
            supplierProductRows={supplierProductRows}
            supplierProductsDropdownItems={supplierProductsDropdownItems}
            supplierProductsTotal={supplierProductsTotal}
          />
        </TabPanel>
        <TabPanel index={1} value={selectedTab}>
          <RecipesGrid
            canCorrectLosses={canCorrectLosses}
            canCreateOrUpdateLosses={canCreateOrUpdateLosses}
            channels={channels}
            currency={currency}
            forbiddenDates={oldDatesWithLosses}
            hasMultipleChannels={hasMultipleChannels}
            isCentralMode={isCentralMode}
            isLoading={isLoading}
            lossCategories={lossCategories}
            recipeRows={recipeRows}
            recipesDropdownItems={recipesDropdownItems}
            recipesTotal={recipesTotal}
            selectedDate={selectedDate}
            selectedStore={selectedStore}
            setIsDirty={setIsDirty}
            setRecipeRows={setRecipeRows}
            setRecipesDropdownItems={setRecipesDropdownItems}
            setRecipesTotal={setRecipesTotal}
            setShouldPictureFullScreen={setShouldPictureFullScreen}
          />
        </TabPanel>
      </GenericContentContainer>
      {renderPictureFullscreen()}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  stores: getIsCentralMode()
    ? getCentralKitchenStores(state.baseReducer.activeStores)
    : getSalesPointStores(state.baseReducer.activeStores),
  currency: state.baseReducer.currency,
  authorizedSalesPointActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/stocks/inventories/losses',
  ),
  authorizedCentralKitchenActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/central-kitchen-stocks/losses',
  ),
  modal: state.modalReducer,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(LossDataGrid);
